/** Subnet / gateway / netmask / pool / speed-limit facts for the tenant's
 *  network. Moved from `pages/network.tsx` when the page split into tabs
 *  (#310); renders on the Connect tab. */
import { ArrowDown, ArrowUp, Globe } from "lucide-react";
import { Card, Group, SimpleGrid, Text } from "@mantine/core";
import type { NetworkInfo } from "@/lib/api/client";
import { cidrToDottedNetmask, conventionalDhcpPool, prefixBase } from "./shared";

export function NetworkInfoCard({ network }: { network: NetworkInfo }) {
  if (!network.prefix) return null;

  const base = prefixBase(network.prefix);
  const subnet = `${network.prefix}${network.netmask != null ? `/${network.netmask}` : ""}`;
  const gateway = `${base}.1`;
  const netmaskDotted = network.netmask != null ? cidrToDottedNetmask(network.netmask) : null;
  const pool = conventionalDhcpPool(network.prefix);

  const hasSpeedLimit = network.speed_limit_download != null || network.speed_limit_upload != null;

  return (
    <Card withBorder padding="lg">
      <Group gap="xs" mb="sm">
        <Globe size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
        <Text fw={500}>Network Information</Text>
      </Group>
      <SimpleGrid cols={2} spacing="xs" verticalSpacing="xs" fz={14}>
        <Text c="dimmed">Subnet</Text>
        <Text ff="monospace">{subnet}</Text>

        <Text c="dimmed">Gateway</Text>
        <Text ff="monospace">{gateway}</Text>

        {netmaskDotted && (
          <>
            <Text c="dimmed">Netmask</Text>
            <Text ff="monospace">{netmaskDotted}</Text>
          </>
        )}

        {base && (
          <>
            <Text c="dimmed">Static IPs</Text>
            <Text ff="monospace">{base}.10 - {base}.49</Text>

            <Text c="dimmed">DHCP IPs</Text>
            <Text ff="monospace">{pool.start} - {pool.end}</Text>
          </>
        )}

        {hasSpeedLimit && (
          <>
            <Text c="dimmed">Speed limit</Text>
            <Group gap="sm" wrap="wrap">
              {network.speed_limit_download != null && (
                <Text ff="monospace" style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <ArrowDown size={12} style={{ color: "var(--mantine-color-blue-6)" }} />
                  {network.speed_limit_download} Mbps
                </Text>
              )}
              {network.speed_limit_upload != null && (
                <Text ff="monospace" style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <ArrowUp size={12} style={{ color: "var(--mantine-color-green-6)" }} />
                  {network.speed_limit_upload} Mbps
                </Text>
              )}
            </Group>
          </>
        )}
      </SimpleGrid>
    </Card>
  );
}
